import { useEffect, useState, type SyntheticEvent } from 'react'
import { EMAIL_PREVIEW_WIDTH } from './emailBrandFacts'
import { Button, Inline, Modal, PreviewFrame, Stack } from '../../ds'

/**
 * The shopper email, previewed in a narrow column (MAR-399, MAR-400): drawn at its own 640px
 * and scaled into the box, the box fitted to the scaled email (capped at `height`, past which it
 * scrolls), and a "View full size" that opens the same document at 640px in a modal — scaled
 * again only when the window itself is narrower.
 *
 * Only the frame is the app's (D-42): `html` is the shared renderer's output, unchanged.
 *
 * ⚠️ Both frames are SANDBOXED (`EMAIL_SANDBOX`): no script in the email runs, and nothing in it
 * can reach the app. `allow-same-origin` is kept on purpose — without it the document cannot be
 * measured and the fit falls back to the fixed heights — which is safe only because scripts are
 * off. Links open a new tab (a `<base target="_blank">` added on load) rather than navigating
 * the preview away from the email.
 */
interface EmailPreviewProps {
  html: string
  /** The cap on the fitted box, in px. */
  height: number
  /** The cap under 680px. */
  compactHeight: number
}

const TITLE = 'Shopper email preview'
/** No scripts; measurable; a link may open a tab, and that tab is a normal page. */
const EMAIL_SANDBOX = 'allow-same-origin allow-popups allow-popups-to-escape-sandbox'

/** The email's document, when the app can read it (it can: same-origin srcDoc). */
const docOf = (e: SyntheticEvent<HTMLIFrameElement>): Document | null => {
  try {
    return e.currentTarget.contentDocument
  } catch {
    return null
  }
}

/** Links in the preview open a tab, never replace the email inside the frame. */
const linksToNewTab = (doc: Document | null) => {
  if (!doc?.head || doc.querySelector('base[target]')) return
  const base = doc.createElement('base')
  base.target = '_blank'
  doc.head.prepend(base)
}

export default function EmailPreview({ html, height, compactHeight }: EmailPreviewProps) {
  const [full, setFull] = useState(false)
  // Mounted on the first open and kept: unmounting on close would blank the modal during its
  // fade-out (the ds Modal has no onExited). A closed modal is visibility:hidden, so the kept
  // frame is neither seen nor focusable; never opened, no second copy of the email loads.
  const [opened, setOpened] = useState(false)
  /** The full-size frame's loaded document — a new srcDoc re-loads and replaces it. */
  const [fullDoc, setFullDoc] = useState<Document | null>(null)

  // Escape inside the email: a keydown in the frame's document never reaches the app's, where
  // the Modal listens, so once the merchant clicks into the email Escape would do nothing.
  useEffect(() => {
    if (!full || !fullDoc) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFull(false) }
    fullDoc.addEventListener('keydown', onKey)
    return () => fullDoc.removeEventListener('keydown', onKey)
  }, [full, fullDoc])

  const open = () => { setOpened(true); setFull(true) }

  return (
    <Stack gap="sm">
      <PreviewFrame title={TITLE} srcDoc={html} sandbox={EMAIL_SANDBOX} height={height} compactHeight={compactHeight}
        naturalWidth={EMAIL_PREVIEW_WIDTH} fitContent onLoad={(e) => linksToNewTab(docOf(e))} />
      <Inline justify="end">
        <Button variant="ghost" size="sm" aria-label="View full size email preview" aria-haspopup="dialog" onClick={open}>
          View full size
        </Button>
      </Inline>
      <Modal
        open={full}
        onClose={() => setFull(false)}
        title={TITLE}
        description={`At its full ${EMAIL_PREVIEW_WIDTH}px width, scaled down only when the window is narrower.`}
        size="lg"
        actions={<Button variant="ghost" size="sm" onClick={() => setFull(false)}>Close</Button>}
      >
        {opened && (
          <PreviewFrame title={`${TITLE}, full size`} srcDoc={html} sandbox={EMAIL_SANDBOX}
            height="calc(100vh - 220px)" compactHeight="calc(100vh - 200px)" naturalWidth={EMAIL_PREVIEW_WIDTH} fitContent
            onLoad={(e) => { const doc = docOf(e); linksToNewTab(doc); setFullDoc(doc) }} />
        )}
      </Modal>
    </Stack>
  )
}
